import { FC, useEffect } from 'react';

export interface PaginationProps {
  page: number;
  pageCount: number;
  onPage: (page: number) => void;
}

const PAGE_BTN_BASE: React.CSSProperties = {
  minWidth: 30,
  height: 30,
  padding: '0 8px',
  fontSize: 13,
  border: '1px solid #E2E5EA',
  borderRadius: 4,
  background: '#fff',
  color: '#667085',
  cursor: 'pointer',
};

const PAGE_BTN_ACTIVE: React.CSSProperties = {
  ...PAGE_BTN_BASE,
  background: '#EAA827',
  borderColor: '#EAA827',
  color: '#fff',
  fontWeight: 700,
  cursor: 'default',
};

const PAGE_BTN_DISABLED: React.CSSProperties = {
  ...PAGE_BTN_BASE,
  color: '#D0D5DD',
  cursor: 'not-allowed',
};

/**
 * Prev/Next + numbered pages (condensed with ellipses beyond 7 pages),
 * README §Interactions & Behavior "Pagination: 8 per page, Prev/Next +
 * numbered pages (condensed with ellipses beyond 7 pages)". Also defensively
 * clamps `page` back to `pageCount` when the result set shrinks (e.g. a
 * filter change) and the caller hasn't already done so — the "page resets on
 * tab/job/company/topN/search changes" + "clamps when filters shrink the
 * set" rule.
 */
const Pagination: FC<PaginationProps> = ({ page, pageCount, onPage }) => {
  useEffect(() => {
    if (pageCount > 0 && page > pageCount) {
      onPage(pageCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageCount]);

  if (pageCount <= 1) return null;

  const clampedPage = Math.min(page, pageCount);

  const pages: (number | 'ellipsis')[] = [];
  if (pageCount <= 7) {
    for (let p = 1; p <= pageCount; p++) pages.push(p);
  } else {
    pages.push(1);
    const start = Math.max(2, clampedPage - 1);
    const end = Math.min(pageCount - 1, clampedPage + 1);
    if (start > 2) pages.push('ellipsis');
    for (let p = start; p <= end; p++) pages.push(p);
    if (end < pageCount - 1) pages.push('ellipsis');
    pages.push(pageCount);
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 16, justifyContent: 'center' }}>
      <button
        type="button"
        style={clampedPage === 1 ? PAGE_BTN_DISABLED : PAGE_BTN_BASE}
        disabled={clampedPage === 1}
        onClick={() => onPage(clampedPage - 1)}
      >
        Prev
      </button>
      {pages.map((p, idx) =>
        p === 'ellipsis' ? (
          <span key={`ellipsis-${idx}`} style={{ padding: '0 4px', color: '#98A2B3', fontSize: 13 }}>
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            style={p === clampedPage ? PAGE_BTN_ACTIVE : PAGE_BTN_BASE}
            disabled={p === clampedPage}
            onClick={() => onPage(p)}
          >
            {p}
          </button>
        )
      )}
      <button
        type="button"
        style={clampedPage === pageCount ? PAGE_BTN_DISABLED : PAGE_BTN_BASE}
        disabled={clampedPage === pageCount}
        onClick={() => onPage(clampedPage + 1)}
      >
        Next
      </button>
    </div>
  );
};

export default Pagination;
